/**
 * Centralized lifecycle-status presentation (design.md §6 "Statuses and
 * feedback": "Lifecycle mappings are centrally defined; unknown server
 * statuses render 'Estado desconocido' rather than inheriting a misleading
 * color"). Pure mapping — no rendering — consumed by `StatusBadge` and any
 * future feature that displays `campaign.estado` (this file's whole point is
 * to be the ONE place this mapping lives, not a per-screen copy).
 */

/**
 * `live` (panel-redesign-v3 T1, REDISENO-PANEL.md §2 pill table) is the
 * one actively-in-progress tone — `enviando` only — distinct from
 * `warning` (which `parcial`/"Completada con errores" keeps).
 */
export type StatusTone = "positive" | "warning" | "critical" | "info" | "unavailable" | "live";

export interface LifecycleStatusPresentation {
  label: string;
  tone: StatusTone;
}

/**
 * The 7 states of `campaign.estado` (design.md §5.1 item 2 /
 * `AdminOverviewLifecycleStates.All`), same order. Labels/tones per
 * REDISENO-PANEL.md §2 ("Estados como pill") and §9.3 ("Completada con
 * errores", antes "Parcial").
 */
const LIFECYCLE_STATUSES: Record<string, LifecycleStatusPresentation> = {
  borrador: { label: "Borrador", tone: "info" },
  programada: { label: "Programada", tone: "info" },
  enviando: { label: "Enviando", tone: "live" },
  parcial: { label: "Completada con errores", tone: "warning" },
  fallida: { label: "Fallida", tone: "critical" },
  completada: { label: "Completada", tone: "positive" },
  cancelada: { label: "Cancelada", tone: "unavailable" },
};

/** Unknown server statuses fall back to a neutral tone and explicit "unknown" copy — never inherit a misleading color by falling through to `undefined`/default styling. */
export function resolveLifecycleStatus(estado: string): LifecycleStatusPresentation {
  return LIFECYCLE_STATUSES[estado] ?? { label: "Estado desconocido", tone: "unavailable" };
}

/**
 * A campaign settles once it reaches one of these three states — every
 * screen that polls `estado` for progress (`CampaignMonitoringTab`,
 * `CampaignListScreen`) stops once it does (design.md §7 "polling... stops
 * for terminal states"). `borrador`/`programada`/`enviando`/`parcial` can
 * still change, so they keep polling. Centralized here (this file's whole
 * point, per its own docstring) so two independent polling call sites can
 * never silently drift into disagreeing about which states are terminal.
 */
const TERMINAL_LIFECYCLE_STATES = new Set(["completada", "cancelada", "fallida"]);

export function isTerminalLifecycleState(estado: string): boolean {
  return TERMINAL_LIFECYCLE_STATES.has(estado);
}

/** Server-owned lifecycle order (design.md §5.1 item 2 / `AdminOverviewLifecycleStates.All`) — the execution status matrix renders in this exact, fixed order regardless of what order the backend response happens to list states in. */
export const LIFECYCLE_STATE_ORDER = [
  "borrador",
  "programada",
  "enviando",
  "parcial",
  "fallida",
  "completada",
  "cancelada",
] as const;
